import type { FC } from 'react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Badge,
    Box,
    IconButton,
    Typography,
    Popover,
    List,
    ListItem,
    Button,
    Chip,
    Divider,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Tooltip,
    Avatar,
    CircularProgress
} from '@material-ui/core';
import type { SxProps } from '@material-ui/system';
import { usePopover } from '../hooks/use-popover';
import { Mail as MailIcon } from '../icons/mail';
import { Check as CheckIcon } from '../icons/check';
import { X as XIcon } from '../icons/x';
import { projectRespondToInvitation, getPendingInvitations } from '../api/project';
import { useSubscriptionStatus } from '../hooks/use-subscription-status';
import { LimitMessageWithUpgrade } from './subscription/limit-message-with-upgrade';
import { formatDate } from '../utils/format-date';
import toast from 'react-hot-toast';

interface PendingInvitationsPopoverProps {
    sx?: SxProps;
}

interface PendingInvitation {
    id: string;
    project: {
        id: string;
        name: string;
        description: string;
    };
    project_admin_space: number;
    review_1: number;
    review_2: number;
    review_3: number;
    review_4: number;
    status: number;
    created_at: string;
}

export const PendingInvitationsPopover: FC<PendingInvitationsPopoverProps> = (props) => {
    const { t } = useTranslation();
    const { hasPremium } = useSubscriptionStatus();
    const [anchorRef, open, handleOpen, handleClose] = usePopover();
    const [invitations, setInvitations] = useState<PendingInvitation[]>([]);
    const [loading, setLoading] = useState(false);

    const loadInvitations = async () => {
        try {
            setLoading(true);
            console.log('Loading pending invitations...');
            const response = await getPendingInvitations();
            console.log('Pending invitations response:', response);
            setInvitations(response?.invitations || []);
        } catch (error) {
            console.error('Failed to load pending invitations:', error);
            setInvitations([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadInvitations();
        // Poll for new invitations every 30 seconds
        const interval = setInterval(loadInvitations, 30000);
        return () => clearInterval(interval);
    }, []);

    const handleAcceptInvitation = async (invitationId: string, projectName: string) => {
        try {
            console.log('Accepting invitation:', invitationId);
            const result = await projectRespondToInvitation(invitationId, {
                email: 'dummy@example.com', // Backend gets email from session
                answer: 'accept'
            });

            if (result.success) {
                toast.success(t('invitation_accepted_successfully'));
                // Refresh the invitations list
                await loadInvitations();
            } else {
                toast.error(t('failed_to_accept_invitation'));
            }
        } catch (err: any) {
            console.error('Error accepting invitation:', err);
            toast.error(t('failed_to_process_invitation'));
        }
    };

    const handleDeclineInvitation = async (invitationId: string) => {
        try {
            console.log('Declining invitation:', invitationId);
            const result = await projectRespondToInvitation(invitationId, {
                email: 'dummy@example.com', // Backend gets email from session
                answer: 'decline'
            });

            if (result.success) {
                toast.success(t('invitation_declined_successfully'));
                // Refresh the invitations list
                await loadInvitations();
            } else {
                toast.error(t('failed_to_decline_invitation'));
            }
        } catch (err: any) {
            console.error('Error declining invitation:', err);
            toast.error(t('failed_to_process_invitation'));
        }
    };

    const getRoleDescription = (projectAdminSpace: number): string => {
        switch (projectAdminSpace) {
            case 1:
                return t('editor_full_access');
            case 2:
                return t('viewer_read_only');
            case 3:
                return t('restricted_no_admin');
            default:
                return t('team_member');
        }
    };

    const handlePopoverOpen = () => {
        handleOpen();
        loadInvitations();
    };

    const invitationCount = invitations.length;

    return (
        <>
            <Badge
                badgeContent={invitationCount > 0 ? invitationCount : undefined}
                color="primary"
                {...props}
            >
                <Tooltip title={t('pending_invitations')} placement="bottom">
                    <IconButton
                        onClick={handlePopoverOpen}
                        ref={anchorRef}
                        sx={{
                            backgroundColor: 'background.default',
                            color: 'primary.main',
                            boxShadow: 3,
                            border: '1px solid',
                            borderColor: 'divider',
                            width: 40,
                            height: 40,
                            '&:hover': {
                                backgroundColor: 'primary.main',
                                color: 'primary.contrastText',
                                boxShadow: 6,
                                transform: 'scale(1.05)',
                                borderColor: 'primary.main'
                            },
                            transition: 'all 0.2s ease-in-out'
                        }}
                    >
                        <MailIcon />
                    </IconButton>
                </Tooltip>
            </Badge>
            <Popover
                anchorEl={anchorRef.current}
                anchorOrigin={{
                    horizontal: 'center',
                    vertical: 'bottom'
                }}
                keepMounted
                onClose={handleClose}
                open={open}
                transformOrigin={{
                    horizontal: 'center',
                    vertical: 'top'
                }}
                PaperProps={{
                    sx: {
                        width: 400,
                        maxHeight: 600,
                        mt: 1,
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                    }
                }}
            >
                <Box sx={{ p: 3, background: 'linear-gradient(135deg, #4970ff 0%, #667eea 100%)', color: 'white' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {t('pending_invitations')} {invitationCount > 0 && `(${invitationCount})`}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        {t('no_pending_invitations_description')}
                    </Typography>
                </Box>
                <Divider />
                <List sx={{ maxHeight: 500, overflow: 'auto', py: 1 }}>
                    {loading ? (
                        <ListItem sx={{ justifyContent: 'center', py: 4 }}>
                            <CircularProgress size={24} sx={{ mr: 2 }} />
                            <Typography color="textSecondary" sx={{ fontStyle: 'italic' }}>
                                {t('loading')}
                            </Typography>
                        </ListItem>
                    ) : (!invitations || invitations.length === 0) ? (
                        <ListItem sx={{ justifyContent: 'center', py: 6, flexDirection: 'column' }}>
                            <MailIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                            <Typography color="textSecondary" variant="h6" sx={{ fontWeight: 500, mb: 1 }}>
                                {t('no_pending_invitations')}
                            </Typography>
                            <Typography color="textSecondary" variant="body2">
                                {t('no_pending_invitations_description')}
                            </Typography>
                        </ListItem>
                    ) : (
                        invitations.map((invitation) => (
                            <ListItem
                                key={invitation.id}
                                sx={{
                                    py: 2,
                                    px: 2,
                                    borderBottom: '1px solid',
                                    borderColor: 'divider'
                                }}
                            >
                                <Box sx={{ width: '100%' }}>
                                    {/* Project Header */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <Avatar
                                            sx={{
                                                backgroundColor: 'primary.main',
                                                mr: 2,
                                                width: 40,
                                                height: 40
                                            }}
                                        >
                                            {invitation.project.name.charAt(0).toUpperCase()}
                                        </Avatar>
                                        <Box sx={{ flexGrow: 1 }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                {invitation.project.name}
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary">
                                                {invitation.project.description}
                                            </Typography>
                                        </Box>
                                        <Chip
                                            color="warning"
                                            label={t('pending')}
                                            size="small"
                                        />
                                    </Box>

                                    {/* Invitation Details */}
                                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                        {t('invited_as')}: {getRoleDescription(invitation.project_admin_space)}
                                    </Typography>

                                    <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 2 }}>
                                        {t('invited_on')}: {formatDate(invitation.created_at)}
                                    </Typography>

                                    {/* Action Buttons */}
                                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                        {hasPremium ? (
                                            <Button
                                                color="primary"
                                                size="small"
                                                variant="contained"
                                                startIcon={<CheckIcon />}
                                                onClick={() => handleAcceptInvitation(invitation.id, invitation.project.name)}
                                            >
                                                {t('accept')}
                                            </Button>
                                        ) : (
                                            <Tooltip title={<LimitMessageWithUpgrade message={t('premium_subscription_required_tooltip')} />}>
                                                <span>
                                                    <Button
                                                        color="primary"
                                                        size="small"
                                                        variant="contained"
                                                        disabled
                                                    >
                                                        {t('accept')}
                                                    </Button>
                                                </span>
                                            </Tooltip>
                                        )}
                                        <Button
                                            color="error"
                                            size="small"
                                            variant="outlined"
                                            startIcon={<XIcon />}
                                            onClick={() => handleDeclineInvitation(invitation.id)}
                                        >
                                            {t('decline')}
                                        </Button>
                                    </Box>
                                </Box>
                            </ListItem>
                        ))
                    )}
                </List>
            </Popover>
        </>
    );
};
